import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import { 
  Phone, Star, Clock, MapPin, ArrowLeft, MessageCircle, 
  ShieldCheck, ChevronLeft, ChevronRight, Globe, Mail, Briefcase , Award, Send
} from "lucide-react";

import { useDispatch, useSelector } from "react-redux";
import { fetchMerchantDetail } from "../../features/home/merchantListingSlice";
import { addOrUpdateRatingThunk, fetchRatingsByMerchantThunk } from "../../features/rating/ratingThunks";
import { tokenManager } from "../../utils/tokenManager";
import useMerchantDetailRedirect from "../../hooks/useMechantDetailRedirect"
import { FaWhatsapp } from "react-icons/fa";



export default function MerchantDetail() {
  // 1. ALL HOOKS MUST BE AT THE TOP (Before any early returns)
  const { id } = useParams();
  
  useMerchantDetailRedirect(id);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { merchantDetail: merchant, loading, error } = useSelector((state) => state.merchantListing);
  const { submitting, error: ratingError } = useSelector((state) => state.rating);

  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Fetch Merchant Detail
  useEffect(() => {
    if (id) {
      dispatch(fetchMerchantDetail(id));
    }
  }, [id, dispatch]);

  // Sync initial rating if user has already rated
  useEffect(() => {
    if (merchant?.rating?.yourRating) {
      setRating(merchant.rating.yourRating);
    }
  }, [merchant]);

  // Error Handling for Rating
  useEffect(() => {
    if (ratingError) {
      toast.error(ratingError);
    }
  }, [ratingError]);

  // 2. EARLY RETURNS (After Hooks)
  if (loading) return <div style={styles.loader}><div className="spinner"></div></div>;
  if (error) return <div style={styles.errorState}>{typeof error === "string" ? error : "Error loading merchant"}</div>;
  if (!merchant) return <div style={styles.errorState}>Merchant not found</div>;

  // 3. HANDLERS
  const IMG_URL = process.env.REACT_APP_IMAGE_BASE_URL;
  const coverImages = merchant.quickInfo?.quickInfoCoverImages || [];

  const handleNext = () => setCurrentImageIndex((p) => (p + 1) % coverImages.length);
  const handlePrev = () => setCurrentImageIndex((p) => (p === 0 ? coverImages.length - 1 : p - 1));


 const handleSubmitRating = () => {
  if (!rating) {
    toast.error("Please select a rating first");
    return;
  }

  const merchantId = merchant?.userId; // ✅ FIX

  if (!merchantId) {
    toast.error("Merchant ID not found");
    return;
  }

  dispatch(
    addOrUpdateRatingThunk({
      merchantId,
      rating,
    })
  ).then((res) => {
    if (res.meta.requestStatus === "fulfilled") {
      toast.success("Rating submitted successfully");

      // refresh merchant detail to show updated rating
      dispatch(fetchMerchantDetail(id));
    }
  });
};

  return (
    <div style={styles.pageWrapper}>
      {/* 1. PREMIUM HERO SECTION */}
      <section style={styles.heroSection}>
        <div style={styles.heroContainer}>
          <div style={styles.topNav}>
            <button onClick={() => window.history.back()} style={styles.backBtn}>
              <ArrowLeft size={18} /> Back
            </button>
            {/* <div style={styles.statusBadge}>
              <ShieldCheck size={14}/> Verified {merchant.status}
            </div> */}
          </div>

          <div className="hero-grid">
            {/* Gallery Side */}
            <div style={styles.galleryContainer}>
              <div style={styles.mainStage}>
                <img 
                  src={coverImages[currentImageIndex] ? `${IMG_URL}${coverImages[currentImageIndex]}` : "https://via.placeholder.com/800x500"} 
                  style={styles.stageImg} alt="Contain"
                />
                {coverImages.length > 1 && (
                  <div style={styles.navArrows}>
                    <button onClick={handlePrev} style={styles.arrowBtn}><ChevronLeft/></button>
                    <button onClick={handleNext} style={styles.arrowBtn}><ChevronRight/></button>
                  </div>
                )}
              </div>
              <div style={styles.thumbStrip} className="hide-scrollbar">
                {coverImages.map((img, i) => (
                  <img
                    key={i} src={`${IMG_URL}${img}`}
                    onClick={() => setCurrentImageIndex(i)}
                    style={{...styles.thumb, border: i === currentImageIndex ? '2px solid var(--color-primary)' : '2px solid transparent'}}
                    loading="lazy"
                    decoding="async"
                  />
                ))}
              </div>
            </div>

            {/* Info Side */}
            <div style={styles.heroText}>
              <div style={styles.logoRow}>
                <div style={styles.logoWrapper}>
                  <img src={`${IMG_URL}${merchant.businessLogo}`} style={styles.logoImg} alt="Logo" />
                </div>
                <div>
                  <h1 style={styles.businessName }>{merchant.businessName || "N/A"}</h1> 
                  <p style={styles.businessType}>{merchant.businessType || "N/A"} • {merchant.quickInfo?.speciality || "N/A"}</p>
                    <div style={styles.statusBadge}>
              <ShieldCheck size={14}/> Verified {merchant.status}
            </div>
                </div>
              </div>

              <div style={styles.metaCloud}>
                <span style={styles.tag}><MapPin size={14}/> {merchant.location || "N/A"}</span>
                <span style={styles.tag}><Star size={14} fill="#fbbf24" color="#fbbf24"/> {merchant.overview?.rating} Rating</span>
                <span style={styles.tag}><Briefcase size={14}/> {merchant.overview?.experienceInYears || "N/A"} Yrs Exp.</span>
              </div>

              <div style={styles.serviceBox}>
                <h5 style={{fontSize: '12px', marginBottom: '8px', opacity: 0.8}}>SERVICES</h5>
                <div style={styles.serviceTags}>
                  {merchant.quickInfo?.services?.map((s, i) => (
                    <span key={i} style={styles.sTag}>{s}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. CONTENT GRID */}
      <div style={styles.mainContent} className="main-grid">
        <div style={styles.leftColumn}>
          <div style={styles.card}>
            <h3 style={styles.cardHeader}>About</h3>
            <p style={styles.description}>{merchant.quickInfo?.detailsOfClientsServed || "N/A"}</p>
            
            <div style={styles.infoGrid}>
              <div style={styles.infoCell}>
                <Clock color="var(--color-primary)" size={20}/>
                <div>
                  <div style={styles.cellLabel}>Working Hours</div>
                  <div style={styles.cellVal}>{merchant.overview?.workingHoursStart || "N/A"} - {merchant.overview?.workingHoursEnd || "N/A"}</div>
                </div>
              </div>
              <div style={styles.infoCell}>
                <Mail color="var(--color-primary)" size={20}/>
                <div>
                  <div style={styles.cellLabel}>Email Address</div>
                  <div style={styles.cellVal}>{merchant.email || "N/A"}</div>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.card}>
            <h3 style={styles.cardHeader}>Catalogue</h3>
            <div className="portfolio-grid">
              {merchant.catalogue?.map((item) => (
                <div key={item._id} style={styles.portItem}>
                  <img src={`${IMG_URL}${item.imageUrl}`} style={styles.portImg} alt={item.name} loading="lazy" decoding="async" />
                  <div style={styles.portLabel}>
                    <div style={{fontWeight: '700'}}>{item.name}</div>
                    <div style={{fontSize: '10px'}}>{item.location}</div>
                  </div>
                </div>
              ))} 
            </div>
          </div>
        </div>

        <div style={styles.rightColumn}>
          <div style={styles.stickySide}>
            <div style={styles.contactCard}>
              <div style={styles.ownerInfo}>
                <img src={`${IMG_URL}${merchant.ownerPhoto}`} style={styles.ownerAvatar} alt="Owner" loading="lazy" decoding="async" />
                <div>
                  <div style={{fontWeight: '700'}}>{merchant.fullName || "N/A"}</div>
                  <div style={{fontSize: '12px', opacity: 0.7}}>Merchant Owner</div>
                </div>
              </div>
              
              <a href={`tel:${merchant.mobileNumber || "N/A"}`} style={styles.primeBtn}>
                <Phone size={18}/> Call Now : {merchant.mobileNumber || "N/A"}
              </a>
              <a href={`https://wa.me/${merchant.overview?.whatsappNumber || "N/A"}`} target="_blank" style={styles.secBtn}>
                <FaWhatsapp size={20} className="text-green-500"/> WhatsApp
              </a>

              <div style={styles.socialRow}>
                {merchant.overview?.socialMediaLinks?.map((link, i) => (
                  <a key={i} href={link} target="_blank" style={styles.socialLink}><Globe size={18}/></a>
                ))}
              </div>
            </div>

            <div style={styles.ratingCard}>
              <h4 style={{fontSize: '16px', marginBottom: '5px'}}>Rate this business</h4>
              <p style={{fontSize: '12px', color: 'var(--color-text-light)', marginBottom: '15px'}}>How was your experience?</p>
              
              <div style={styles.starRow}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={i} size={30}
                    style={{cursor: 'pointer', transition: '0.2s'}}
                    fill={i <= (hoverRating || rating) ? "#fbbf24" : "none"}
                    color={i <= (hoverRating || rating) ? "#fbbf24" : "var(--color-border)"}
                    onMouseEnter={() => setHoverRating(i)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setRating(i)}
                  />
                ))}
              </div>
              <button 
                onClick={handleSubmitRating}
                disabled={submitting}
                style={{
                  ...styles.submitBtn,
                  backgroundColor: submitting ? "var(--color-placeholder)" : "var(--color-primary)"
                }}
              >
                {submitting ? "Submitting..." : "Submit Rating"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        :root {
          --color-primary: #6052a5;
          --color-primary-dark: #4a3f85;
          --color-primary-light: #4B3D9A;
          --color-secondary: #e5e7eb;
          --color-text-dark: #1f2937;
          --color-text-light: #6b7280;
          --color-background-light: #f9fafb;
          --color-danger: #ef4444;
          --color-border: #bdc3c7;
        }

         .hero-grid {
          display: grid;
          grid-template-columns: 1.2fr 1fr;
          gap: 40px;
        }

        .main-grid {
          display: grid;
          grid-template-columns: 1fr 350px;
          gap: 30px;
          margin-top: -50px;
        }

        @media (max-width: 1200px) {
          .hero-grid {
            grid-template-columns: 1fr 1fr;
          }
        }

        @media (max-width: 992px) {
          .hero-grid,
          .main-grid {
            grid-template-columns: 1fr;
          }

          .main-grid {
            margin-top: 20px;
          }

          .stickySide {
            position: static !important;
          }
        }

        @media (max-width: 768px) {
          h1 {
            font-size: 1.6rem !important;
          }

          .thumbStrip img {
            width: 60px !important;
            height: 45px !important;
          }

          button {
            width: 100%;
          }
        }

        @media (max-width: 576px) {
          .heroSection {
            padding: 25px 0 60px 0;
          }

          .mainStage {
            height: 240px !important;
          }

          .tag {
            font-size: 11px;
          }
        }

        @keyframes spin {
          100% { transform: rotate(360deg); }
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid var(--color-secondary);
          border-top-color: var(--color-primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .hero-grid { display: grid; grid-template-columns: 1.2fr 1fr; gap: 40px; }
        .main-grid { display: grid; grid-template-columns: 1fr 350px; gap: 30px; margin-top: -50px; }
        .portfolio-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 15px; }
        .hide-scrollbar::-webkit-scrollbar { display: none; }

        @media (max-width: 1024px) {
          .hero-grid, .main-grid { grid-template-columns: 1fr; }
          .main-grid { margin-top: 20px; }
        }

        @keyframes spin { 100% { transform: rotate(360deg); } }
        .spinner { width: 40px; height: 40px; border: 4px solid var(--color-secondary); border-top-color: var(--color-primary); border-radius: 50%; animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}

const styles = {
  pageWrapper: { backgroundColor: "var(--color-background-light)", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
  
  // HERO
  heroSection: { backgroundColor: "var(--color-primary-dark)", padding: "40px 0 100px 0", color: "white" },
  heroContainer: { maxWidth: "1200px", margin: "0 auto", padding: "0 20px" },
  topNav: { display: "flex", justifyContent: "space-between", marginBottom: "30px" },
  backBtn: { background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "8px 16px", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" },
  statusBadge: { backgroundColor: "#10b981", color: "white", padding: "6px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: "600" },

  galleryContainer: { width: "100%" },
  mainStage: { height: "380px", borderRadius: "20px", overflow: "hidden", position: "relative", boxShadow: "0 10px 30px rgba(0,0,0,0.2)" },
  stageImg: { width: "100%", height: "100%", objectFit: "contain" },
  navArrows: { position: "absolute", inset: 0, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 15px" },
  arrowBtn: { background: "rgba(0,0,0,0.3)", border: "none", color: "white", width: "40px", height: "40px", borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  thumbStrip: { display: "flex", gap: "10px", marginTop: "12px", overflowX: "auto" },
  thumb: { width: "70px", height: "50px", borderRadius: "8px", objectFit: "contain", cursor: "pointer", transition: "0.2s" },

  heroText: { display: "flex", flexDirection: "column", gap: "20px" },
  logoRow: { display: "flex", alignItems: "center", gap: "20px" },
  logoWrapper: { width: "70px", height: "70px", background: "white", borderRadius: "15px", padding: "5px" },
  logoImg: { width: "100%", height: "100%", objectFit: "contain", borderRadius: "10px" },
  businessName: { fontSize: "2.5rem", fontWeight: "800", margin: 0 },
  businessType: { color: "#e0e7ff", fontSize: "16px" },
  metaCloud: { display: "flex", gap: "10px", flexWrap: "wrap" },
  tag: { background: "rgba(255,255,255,0.1)", padding: "6px 12px", borderRadius: "8px", fontSize: "13px", display: "flex", alignItems: "center", gap: "6px" },
  serviceTags: { display: "flex", gap: "8px", flexWrap: "wrap" },
  sTag: { background: "var(--color-primary-light)", padding: "4px 12px", borderRadius: "6px", fontSize: "12px", border: "1px solid rgba(255,255,255,0.2)" },

  // MAIN CONTENT
  mainContent: { maxWidth: "1200px", margin: "-50px auto 0", padding: "0 20px 60px", position: "relative" },
  leftColumn: { display: "flex", flexDirection: "column", gap: "25px" },
  card: { backgroundColor: "white", borderRadius: "20px", padding: "30px", boxShadow: "0 4px 6px rgba(0,0,0,0.02)", border: "1px solid var(--color-secondary)" },
  cardHeader: { fontSize: "18px", fontWeight: "700", marginBottom: "20px", color: "var(--color-text-dark)", borderLeft: "4px solid var(--color-primary)", paddingLeft: "12px" },
  description: { lineHeight: "1.7", color: "var(--color-text-light)", fontSize: "15px" },
  infoGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginTop: "25px", paddingTop: "20px", borderTop: "1px solid var(--color-secondary)" },
  infoCell: { display: "flex", gap: "12px", alignItems: "center" },
  cellLabel: { fontSize: "11px", color: "var(--color-text-light)", textTransform: "uppercase" },
  cellVal: { fontSize: "14px", fontWeight: "600", color: "var(--color-text-dark)" },

  portItem: { position: "relative", height: "130px", borderRadius: "12px", overflow: "hidden" },
  portImg: { width: "100%", height: "100%", objectFit: "contain" },
  portLabel: { position: "absolute", bottom: 0, width: "100%", padding: "8px", background: "linear-gradient(transparent, rgba(0,0,0,0.8))", color: "white" },

  // SIDEBAR
  stickySide: { position: "sticky", top: "20px", display: "flex", flexDirection: "column", gap: "20px" },
  contactCard: { backgroundColor: "white", borderRadius: "20px", padding: "25px", border: "1px solid var(--color-secondary)" },
  ownerInfo: { display: "flex", gap: "12px", alignItems: "center", marginBottom: "20px" },
  ownerAvatar: { width: "55px", height: "55px", borderRadius: "50%", border: "2px solid var(--color-primary)" },
  primeBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", width: "100%", background: "var(--color-primary)", color: "white", padding: "14px", borderRadius: "12px", textDecoration: "none", fontWeight: "700", marginBottom: "10px" },
  secBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", width: "100%", background: "white", border: "2px solid var(--color-primary)", color: "var(--color-primary)", padding: "12px", borderRadius: "12px", textDecoration: "none", fontWeight: "700" },
  socialRow: { display: "flex", gap: "10px", marginTop: "20px", justifyContent: "center" },
  socialLink: { width: "38px", height: "38px", borderRadius: "50%", background: "var(--color-background-light)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-primary)" },

  ratingCard: { backgroundColor: "white", borderRadius: "20px", padding: "25px", border: "1px solid var(--color-secondary)" },
  starRow: { display: "flex", gap: "5px", marginBottom: "20px" },
  submitBtn: { width: "100%", padding: "12px", color: "white", border: "none", borderRadius: "10px", fontWeight: "700", cursor: "pointer", transition: "0.3s" },

  loader: { height: "100vh", display: "flex", justifyContent: "center", alignItems: "center" },
  errorState: { textAlign: "center", padding: "100px", color: "var(--color-text-light)" }
};